import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  KeyRound,
  Lock,
  Mail,
  ShieldCheck,
} from "lucide-react";
import { Auth, tokenStorage } from "@/lib/api";
import { useRecaptchaToken, RECAPTCHA_ENABLED } from "@/lib/recaptcha";

/**
 * Admin/Employee password self-service:
 *   /admin/forgot-password   → request reset email
 *   /admin/reset-password    → set new password from emailed link
 *   /admin/change-password   → change password while logged in (requires current)
 */

const Shell = ({ testId, eyebrow, title, children, backTo = "/admin" }) => (
  <div
    data-testid={testId}
    className="min-h-screen bg-[#04102B] text-white flex items-center justify-center px-5 py-10"
  >
    <div className="w-full max-w-md">
      <Link to="/" className="block text-center mb-8">
        <div className="font-display text-3xl tracking-tight">
          Sahyog<span className="text-[#D4AF37]">.</span> Admin
        </div>
        <div className="text-[#D4AF37] text-xs tracking-[0.22em] uppercase mt-1">{eyebrow}</div>
      </Link>
      <div className="bg-[#0A1F44] rounded-3xl p-7 border border-[#D4AF37]/30">
        <h1 className="font-display text-2xl">{title}</h1>
        {children}
      </div>
      <div className="mt-5 text-center">
        <Link
          to={backTo}
          className="inline-flex items-center gap-1.5 text-xs text-white/55 hover:text-[#F0D788]"
        >
          <ArrowLeft size={12} /> Back
        </Link>
      </div>
    </div>
  </div>
);

const RecaptchaNotice = () =>
  RECAPTCHA_ENABLED ? (
    <p className="mt-4 text-[11px] text-white/40 flex items-start gap-1.5 leading-snug">
      <ShieldCheck size={11} className="mt-0.5 text-[#D4AF37] shrink-0" />
      <span>
        Protected by reCAPTCHA — Google{" "}
        <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="underline hover:text-[#F0D788]">Privacy Policy</a>{" "}and{" "}
        <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" className="underline hover:text-[#F0D788]">Terms</a> apply.
      </span>
    </p>
  ) : null;

/* ─── 1 · Forgot password ─────────────────────────────────────────── */

export function AdminForgotPassword() {
  const getRecaptchaToken = useRecaptchaToken();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", message: "Sending reset link…" });
    try {
      const recaptcha_token = await getRecaptchaToken("admin-forgot-password");
      // Backend soft-fails on missing tokens — pass through as-is.
      const r = await Auth.forgotPassword({ email: email.trim(), recaptcha_token });
      setStatus({ state: "success", message: r?.message || "Check your inbox." });
    } catch (err) {
      setStatus({
        state: "error",
        message:
          err?.response?.data?.detail?.message ||
          err?.response?.data?.detail ||
          "Could not send the reset email. Please try again in a moment.",
      });
    }
  };

  return (
    <Shell testId="admin-forgot-page" eyebrow="Admin · Reset Password" title="Forgot your password?" backTo="/admin">
      <p className="text-white/60 text-xs mt-1 leading-relaxed">
        Enter the <strong className="text-white/85">email address</strong> registered on your admin
        account. If it matches, we&apos;ll email you a secure link that expires in 15 minutes.
      </p>

      <form data-testid="admin-forgot-form" onSubmit={submit} className="mt-6">
        <label className="block text-[11px] uppercase tracking-wider text-white/55 mb-2">
          Email address <span className="text-[#D4AF37]">*</span>
        </label>
        <div className="relative">
          <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            data-testid="admin-forgot-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@sahyogtapi.in"
            className="w-full pl-9 pr-4 py-3 rounded-lg bg-white/[0.05] border border-[#D4AF37]/20 outline-none focus:border-[#D4AF37] text-sm"
          />
        </div>

        {status.state === "error" && (
          <div data-testid="admin-forgot-error" className="mt-4 flex items-start gap-2 px-3 py-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-200 text-sm">
            <AlertCircle size={14} className="mt-0.5 shrink-0" /> <span>{status.message}</span>
          </div>
        )}
        {status.state === "success" && (
          <div data-testid="admin-forgot-success" className="mt-4 flex items-start gap-2 px-3 py-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-100 text-sm leading-snug">
            <CheckCircle2 size={14} className="mt-0.5 shrink-0" /> <span>{status.message}</span>
          </div>
        )}

        <button
          data-testid="admin-forgot-submit"
          type="submit"
          disabled={status.state === "loading"}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full btn-gold font-semibold disabled:opacity-70"
        >
          <KeyRound size={16} /> {status.state === "loading" ? "Sending…" : "Send reset link"}
        </button>

        <RecaptchaNotice />
      </form>
    </Shell>
  );
}

/* ─── 2 · Reset password (from emailed link) ──────────────────────── */

export function AdminResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const getRecaptchaToken = useRecaptchaToken();
  const [form, setForm] = useState({ pw: "", pw2: "" });
  const [status, setStatus] = useState({ state: token ? "idle" : "no-token", message: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (form.pw.length < 8) {
      setStatus({ state: "error", message: "Password must be at least 8 characters." });
      return;
    }
    if (form.pw !== form.pw2) {
      setStatus({ state: "error", message: "Passwords do not match." });
      return;
    }
    setStatus({ state: "loading", message: "Updating password…" });
    try {
      const recaptcha_token = await getRecaptchaToken("admin-reset-password");
      // Backend soft-fails on missing tokens — pass through as-is.
      await Auth.resetPassword({ token, new_password: form.pw, recaptcha_token });
      setStatus({ state: "success", message: "Password updated. Redirecting to sign-in…" });
      setTimeout(() => navigate("/admin", { replace: true }), 2000);
    } catch (err) {
      setStatus({
        state: "error",
        message:
          err?.response?.data?.detail?.message ||
          err?.response?.data?.detail ||
          "Could not reset the password. The link may have expired — please request a new one.",
      });
    }
  };

  if (status.state === "no-token") {
    return (
      <Shell testId="admin-reset-page" eyebrow="Admin · Reset Password" title="Reset link missing" backTo="/admin">
        <p className="text-white/60 text-sm mt-3 leading-relaxed">
          The reset link is missing its security token. Please open the link exactly as it was
          emailed to you, or request a new one.
        </p>
        <Link
          to="/admin/forgot-password"
          className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full btn-gold font-semibold"
        >
          Request a new link
        </Link>
      </Shell>
    );
  }

  return (
    <Shell testId="admin-reset-page" eyebrow="Admin · Reset Password" title="Set a new password" backTo="/admin">
      <p className="text-white/60 text-xs mt-1 leading-relaxed">
        Pick a strong password (min 8 characters, mix letters + numbers + symbols recommended).
      </p>

      <form data-testid="admin-reset-form" onSubmit={submit} className="mt-6">
        <label className="block text-[11px] uppercase tracking-wider text-white/55 mb-2">
          New password <span className="text-[#D4AF37]">*</span>
        </label>
        <input
          data-testid="admin-reset-new-pw"
          type="password"
          required
          minLength={8}
          value={form.pw}
          onChange={(e) => setForm((f) => ({ ...f, pw: e.target.value }))}
          placeholder="At least 8 characters"
          className="w-full px-4 py-3 rounded-lg bg-white/[0.05] border border-[#D4AF37]/20 outline-none focus:border-[#D4AF37] text-sm"
        />

        <label className="block text-[11px] uppercase tracking-wider text-white/55 mt-4 mb-2">
          Confirm new password <span className="text-[#D4AF37]">*</span>
        </label>
        <input
          data-testid="admin-reset-new-pw2"
          type="password"
          required
          minLength={8}
          value={form.pw2}
          onChange={(e) => setForm((f) => ({ ...f, pw2: e.target.value }))}
          placeholder="Type the same password again"
          className="w-full px-4 py-3 rounded-lg bg-white/[0.05] border border-[#D4AF37]/20 outline-none focus:border-[#D4AF37] text-sm"
        />

        {status.state === "error" && (
          <div data-testid="admin-reset-error" className="mt-4 flex items-start gap-2 px-3 py-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-200 text-sm">
            <AlertCircle size={14} className="mt-0.5 shrink-0" /> <span>{status.message}</span>
          </div>
        )}
        {status.state === "success" && (
          <div data-testid="admin-reset-success" className="mt-4 flex items-start gap-2 px-3 py-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-100 text-sm">
            <CheckCircle2 size={14} className="mt-0.5 shrink-0" /> <span>{status.message}</span>
          </div>
        )}

        <button
          data-testid="admin-reset-submit"
          type="submit"
          disabled={status.state === "loading" || status.state === "success"}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full btn-gold font-semibold disabled:opacity-70"
        >
          <KeyRound size={16} /> {status.state === "loading" ? "Updating…" : "Update password"}
        </button>

        <RecaptchaNotice />
      </form>
    </Shell>
  );
}

/* ─── 3 · Change password (logged in) ─────────────────────────────── */

export function AdminChangePassword() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ current: "", pw: "", pw2: "" });
  const [status, setStatus] = useState({ state: "idle", message: "" });

  if (!tokenStorage.get()) {
    return (
      <Shell testId="admin-change-page" eyebrow="Admin · Change Password" title="Sign in first" backTo="/admin">
        <p className="text-white/60 text-sm mt-3">You need to be signed in as an admin to change your password.</p>
      </Shell>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (form.pw.length < 8) {
      setStatus({ state: "error", message: "New password must be at least 8 characters." });
      return;
    }
    if (form.pw !== form.pw2) {
      setStatus({ state: "error", message: "New passwords do not match." });
      return;
    }
    setStatus({ state: "loading", message: "Updating password…" });
    try {
      await Auth.changePassword({ current_password: form.current, new_password: form.pw });
      setStatus({ state: "success", message: "Password updated. You can keep working — your session stays active." });
      setForm({ current: "", pw: "", pw2: "" });
    } catch (err) {
      setStatus({
        state: "error",
        message:
          err?.response?.data?.detail?.message ||
          err?.response?.data?.detail ||
          "Could not update the password.",
      });
    }
  };

  return (
    <Shell testId="admin-change-page" eyebrow="Admin · Change Password" title="Change your password" backTo="/admin/leads">
      <p className="text-white/60 text-xs mt-1 leading-relaxed">
        Enter your current password, then pick a new one (min 8 characters). Your session stays
        active — you don&apos;t need to sign back in.
      </p>

      <form data-testid="admin-change-form" onSubmit={submit} className="mt-6">
        <label className="block text-[11px] uppercase tracking-wider text-white/55 mb-2">
          Current password <span className="text-[#D4AF37]">*</span>
        </label>
        <div className="relative">
          <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            data-testid="admin-change-current"
            type="password"
            required
            value={form.current}
            onChange={(e) => setForm((f) => ({ ...f, current: e.target.value }))}
            className="w-full pl-9 pr-4 py-3 rounded-lg bg-white/[0.05] border border-[#D4AF37]/20 outline-none focus:border-[#D4AF37] text-sm"
          />
        </div>

        <label className="block text-[11px] uppercase tracking-wider text-white/55 mt-4 mb-2">
          New password <span className="text-[#D4AF37]">*</span>
        </label>
        <input
          data-testid="admin-change-new"
          type="password"
          required
          minLength={8}
          value={form.pw}
          onChange={(e) => setForm((f) => ({ ...f, pw: e.target.value }))}
          placeholder="At least 8 characters"
          className="w-full px-4 py-3 rounded-lg bg-white/[0.05] border border-[#D4AF37]/20 outline-none focus:border-[#D4AF37] text-sm"
        />

        <label className="block text-[11px] uppercase tracking-wider text-white/55 mt-4 mb-2">
          Confirm new password <span className="text-[#D4AF37]">*</span>
        </label>
        <input
          data-testid="admin-change-new2"
          type="password"
          required
          minLength={8}
          value={form.pw2}
          onChange={(e) => setForm((f) => ({ ...f, pw2: e.target.value }))}
          className="w-full px-4 py-3 rounded-lg bg-white/[0.05] border border-[#D4AF37]/20 outline-none focus:border-[#D4AF37] text-sm"
        />

        {status.state === "error" && (
          <div data-testid="admin-change-error" className="mt-4 flex items-start gap-2 px-3 py-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-200 text-sm">
            <AlertCircle size={14} className="mt-0.5 shrink-0" /> <span>{status.message}</span>
          </div>
        )}
        {status.state === "success" && (
          <div data-testid="admin-change-success" className="mt-4 flex items-start gap-2 px-3 py-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-100 text-sm">
            <CheckCircle2 size={14} className="mt-0.5 shrink-0" /> <span>{status.message}</span>
          </div>
        )}

        <div className="mt-6 flex gap-2">
          <button
            type="button"
            onClick={() => navigate("/admin/leads")}
            className="px-4 py-3 rounded-full border border-white/15 text-white/75 hover:bg-white/5 text-sm"
          >
            Cancel
          </button>
          <button
            data-testid="admin-change-submit"
            type="submit"
            disabled={status.state === "loading"}
            className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full btn-gold font-semibold disabled:opacity-70"
          >
            <KeyRound size={16} /> {status.state === "loading" ? "Updating…" : "Update password"}
          </button>
        </div>
      </form>
    </Shell>
  );
}
